/**
 * Typed GraphQL reads for the games a place runs (place manager, unit 5).
 * Read-only: nothing in this module writes.
 *
 * Two small reads, the place's first, then the catalog only when the place
 * is visible (games change only when someone edits them, so they are not
 * polled):
 *   1. {@link PlaceGamesQuery}: the place's brand's game list
 *      (`Brand.allowedGameSlugs`: opaque slugs, never checked against the
 *      catalog; empty means the brand's app shows no games) and the game each
 *      room starts on (`resolved*Defaults`: the place's own pick, or the
 *      platform default when it pins none; `defaultActivityFor*` says which).
 *   2. {@link GameCatalogQuery}: the catalog (`activityRegistryEntries`),
 *      for the names and engines the slugs stand for. Any signed-in user reads
 *      the enabled games; staff read every game not archived (`all: true`, an
 *      admin-only argument, through the staff door), so a game switched off
 *      shows as such rather than as not in the catalog. Ordered by the
 *      catalog's own sort. Read once per session per list
 *      ({@link readGameCatalogOnce}), not once per visit.
 * Neither read has a `first:`, so neither is multiplied by the backend's
 * query cost limit; `place-events-api.test.ts` keeps both under it.
 *
 * What the rooms play right now is not read here: it comes from the room-now
 * read (`PlaceFields`, polled).
 */

import { graphql, gqlRequestAs, type RequestOptions, type ResultOf } from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';

/** A game as the catalog lists it: name, slug, engine (`activityServerType`)
 *  and whether it is switched on. */
export const GameFields = graphql(`
  fragment GameFields on ActivityRegistryEntry {
    id
    name
    slug
    activityServerType
    isEnabled
  }
`);

/** A game, as the catalog and the place's defaults return it. */
export type GameData = ResultOf<typeof GameFields>;

/** A place's game settings: its brand's game list, and what its main room
 *  and its tables start on (and whether the place pins that itself). */
export const PlaceGamesFields = graphql(
  `
  fragment PlaceGamesFields on Place {
    id
    brand {
      id
      allowedGameSlugs
    }
    defaultActivityForRootGathering {
      id
    }
    defaultActivityForSubGatherings {
      id
    }
    resolvedRootGatheringDefaults {
      activity {
        ...GameFields
      }
    }
    resolvedSubGatheringDefaults {
      activity {
        ...GameFields
      }
    }
  }
`,
  [GameFields],
);

/** A place's game settings, as the read returns them. */
export type PlaceGamesData = ResultOf<typeof PlaceGamesFields>;

export const PlaceGamesQuery = graphql(
  `
  query PlaceGames($id: ID!) {
    place(id: $id) {
      ...PlaceGamesFields
    }
  }
`,
  [PlaceGamesFields],
);

/** A place's game settings. Null when the place does not exist or the caller
 *  cannot see it. `options.signal` aborts the request (as in the read below). */
export async function fetchPlaceGames(
  placeId: string,
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<PlaceGamesData | null> {
  const data = await gqlRequestAs(viewer, PlaceGamesQuery, { id: placeId }, options);
  return data.place;
}

export const GameCatalogQuery = graphql(
  `
  query GameCatalog($all: Boolean!) {
    activityRegistryEntries(all: $all) {
      nodes {
        ...GameFields
      }
    }
  }
`,
  [GameFields],
);

/** The catalog as read: its games, and `all`, which list was read (true:
 *  every game not archived, through the staff door; false: the enabled games
 *  only). The copy follows `all`, never the viewer, so the two cannot drift. */
export interface GameCatalogData {
  nodes: GameData[];
  all: boolean;
}

/** The catalog: every game not archived for staff (`all`, through the staff
 *  door), the enabled games for anyone else. Archived games are never listed,
 *  whoever reads (the backend leaves them out), so a listed slug naming one
 *  cannot be told from a slug naming no game. */
export async function fetchGameCatalog(viewer: RequestViewer, options?: RequestOptions): Promise<GameCatalogData> {
  const all = viewer.isSuperAdmin;
  const data = await gqlRequestAs(viewer, GameCatalogQuery, { all }, options);
  return { nodes: data.activityRegistryEntries.nodes, all };
}

/** The catalog reads made this session, one per list (`all`). */
const catalogReads = new Map<boolean, Promise<GameCatalogData>>();

/** {@link fetchGameCatalog}, read once per list for the session, so moving
 *  from place to place does not read it again. Not tied to any page's abort
 *  signal (it is shared); a failed read is forgotten, so the next visit tries
 *  again. {@link forgetGameCatalog} clears it on sign-out. */
export function readGameCatalogOnce(viewer: RequestViewer): Promise<GameCatalogData> {
  const all = viewer.isSuperAdmin;
  let read = catalogReads.get(all);
  if (!read) {
    const mine = fetchGameCatalog(viewer);
    mine.catch(() => {
      if (catalogReads.get(all) === mine) catalogReads.delete(all);
    });
    catalogReads.set(all, mine);
    read = mine;
  }
  return read;
}

/** Forget the session's catalog reads (on sign-out). */
export function forgetGameCatalog(): void {
  catalogReads.clear();
}
